import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTuples, type Tuple } from '../../hooks/useTuples';

const CLAIM_TYPES = ['all', 'NEWS', 'QUOTE', 'OFFICIAL', 'BACKGROUND', 'CONTEXT', 'REACTION', 'STATISTIC', 'ALLEGATION', 'PLAN', 'PROCEDURE', 'ANALYSIS'];

function TupleRow({ tuple, orgPrefix }: { tuple: Tuple; orgPrefix: string }) {
  const subjectAttrs = tuple.subject.attributes;
  const objectAttrs = tuple.object?.attributes;

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      {/* Subject */}
      <td className="px-3 py-2">
        <div className="font-medium text-gray-900">{tuple.subject.name}</div>
        <div className="text-xs text-gray-500">{tuple.subject.type}</div>
        {subjectAttrs && Object.keys(subjectAttrs).length > 0 && (
          <div className="text-xs text-blue-600 mt-0.5">
            {Object.entries(subjectAttrs).map(([k, v]) => (
              <span key={k} className="mr-2">{k}: {String(v)}</span>
            ))}
          </div>
        )}
      </td>

      {/* Predicate */}
      <td className="px-3 py-2">
        <span className="font-mono text-sm text-purple-700">{tuple.predicate}</span>
      </td>

      {/* Object */}
      <td className="px-3 py-2">
        {tuple.object ? (
          <>
            <div className="font-medium text-gray-900">{tuple.object.name}</div>
            <div className="text-xs text-gray-500">{tuple.object.type}</div>
            {objectAttrs && Object.keys(objectAttrs).length > 0 && (
              <div className="text-xs text-blue-600 mt-0.5">
                {Object.entries(objectAttrs).map(([k, v]) => (
                  <span key={k} className="mr-2">{k}: {String(v)}</span>
                ))}
              </div>
            )}
          </>
        ) : tuple.value !== null ? (
          <span className="font-mono text-sm">
            {tuple.value} {tuple.unit}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>

      {/* Type */}
      <td className="px-3 py-2">
        <span className={`inline-block px-2 py-0.5 text-xs rounded ${
          tuple.claimType === 'NEWS' ? 'bg-red-100 text-red-700' :
          tuple.claimType === 'QUOTE' ? 'bg-blue-100 text-blue-700' :
          tuple.claimType === 'OFFICIAL' ? 'bg-green-100 text-green-700' :
          tuple.claimType === 'STATISTIC' ? 'bg-yellow-100 text-yellow-700' :
          tuple.claimType === 'ALLEGATION' ? 'bg-orange-100 text-orange-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {tuple.claimType}
        </span>
      </td>

      {/* Location */}
      <td className="px-3 py-2 text-sm text-gray-600">
        {tuple.location || '-'}
      </td>

      {/* Date */}
      <td className="px-3 py-2 text-sm text-gray-600">
        {tuple.eventDate || '-'}
      </td>

      {/* Sources */}
      <td className="px-3 py-2 text-center">
        {tuple.sourceCount && tuple.sourceCount > 1 ? (
          <span className="inline-block px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
            {tuple.sourceCount}
          </span>
        ) : (
          <span className="text-gray-400">1</span>
        )}
      </td>

      {/* Story */}
      <td className="px-3 py-2">
        <Link
          to={`${orgPrefix}/story/${tuple.storyId}`}
          className="text-xs text-blue-600 hover:underline line-clamp-2"
        >
          {tuple.storyTitle}
        </Link>
      </td>
    </tr>
  );
}

export function TuplesPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const [typeFilter, setTypeFilter] = useState('all');
  const [subjectSearch, setSubjectSearch] = useState('');
  const [limit, setLimit] = useState(100);

  const { data, isLoading, isError, error } = useTuples({
    limit,
    type: typeFilter === 'all' ? undefined : typeFilter,
    subject: subjectSearch || undefined,
  });

  const tuples = data?.tuples || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading tuples...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error.message}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Knowledge Graph</h1>
        <p className="text-gray-600">Subject-Predicate-Object triples extracted from news</p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4 p-3 bg-gray-50 rounded-lg">
        {/* Type filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Type:</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            {CLAIM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === 'all' ? 'All Types' : t}
              </option>
            ))}
          </select>
        </div>

        {/* Subject search */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Subject:</span>
          <input
            type="text"
            value={subjectSearch}
            onChange={(e) => setSubjectSearch(e.target.value)}
            placeholder="Search subject..."
            className="text-sm border border-gray-300 rounded px-2 py-1 w-40"
          />
        </div>

        {/* Limit */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Show:</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={500}>500</option>
          </select>
        </div>

        {/* Count */}
        <div className="text-sm text-gray-500 ml-auto">
          {tuples.length} tuples
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Predicate</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Object/Value</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-center">Sources</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Story</th>
              </tr>
            </thead>
            <tbody>
              {tuples.map((tuple) => (
                <TupleRow key={tuple.id} tuple={tuple} orgPrefix={orgPrefix} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {tuples.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No tuples found matching your filters.
        </div>
      )}
    </div>
  );
}
